import {
  findAllMediaByRatioLabel,
  findMediaByRatioLabel,
  findAllVideoMedia,
  humanizeCallToAction,
} from './format'
import { resolveCallToAction } from 'utils/helpers'

// Builds per-placement "ad" variation lists for Google Display Ads placement
// cards (9:16 / 1:1 / 1.91:1 / Video / General) from a v4 creative object.
// Each ratio group renders several skins of the same responsive ad — Display
// Network surfaces the same asset differently (full-bleed, Gmail, YouTube, etc.).

const RATIO_191_MOBILE_SKINS = [
  'bare-gradient', // 31509:16089 (sampled)
  'bare-stacked', // 31510:16122
  'bare-stacked', // 31510:16137 (sampled)
  'bare-stacked', // 31510:16168
  'bare-stacked', // 31511:16189 (no sampled sibling — default skin)
  'youtube-chrome', // 31512:16212 (sampled)
  'interstitial', // 31513:16272
  'interstitial', // 31513:16306 (sampled)
]

const RATIO_191_DESKTOP_NATIVE_SKINS = [
  'native-article', // 31516:16349 (sampled)
  'native-article', // 31516:16381
]

const VIDEO_SKINS = [
  'gmail-chrome', // 31518:16487 desktop-video (sampled)
  'youtube-chrome', // 31518:16515
  'bare-gradient', // 31518:16545 video-display
]

const GENERAL_SKINS = [
  'bare-gradient', // 31518:16562 (unnamed)
  'bare-stacked', // 31518:16596 general-display
  'native-article', // 31518:16611 desktop-general (sampled)
]

const DISPLAY_1X1_SKINS = ['bare-stacked', 'bare-stacked']

const isImageMedia = (media) => Boolean(media?.type?.includes('image'))

// Google's Display/Demand Gen network only ever accepts a YouTube passthrough
// video, never an uploaded video file (config_builder.py's documented
// creative shape has no uploaded-video item shape at all) — so any media item
// typed as video without a youtube_video_id is dropped before it can reach a
// ratio match or findVideoMedia here. Meta's own isVideoMedia/findVideoMedia
// (format.js) stay unchanged since uploaded video is valid there.
const isUploadedVideoMedia = (media) =>
  Boolean(media?.type?.includes('video')) && !media?.youtube_video_id

function getGoogleMedia(creative) {
  return (creative?.media ?? []).filter((media) => !isUploadedVideoMedia(media))
}

function buildDisplayAd(creative, media, skin, t) {
  return {
    skin,
    headline: creative?.headline?.[0] ?? '',
    longHeadline: creative?.long_headline?.[0] ?? '',
    description: creative?.body?.[0] ?? creative?.description ?? '',
    callToAction: humanizeCallToAction(resolveCallToAction(creative?.call_to_action, 'google'), t),
    destinationUrl: creative?.destination_url?.[0] ?? '',
    displayUrlPath: creative?.display_url_path?.[0] ?? null,
    businessName: creative?.business_name ?? '',
    logo: creative?.logo ?? null,
    media,
  }
}

function buildSkinsForMedia(creative, mediaList, skins, t) {
  return mediaList.flatMap((media) =>
    skins.map((skin) => buildDisplayAd(creative, media, skin, t)),
  )
}

export function getDisplay9x16Variations(creative, t) {
  const images = findAllMediaByRatioLabel(getGoogleMedia(creative), '9:16')
  return images.map((media) => buildDisplayAd(creative, media, 'bare-gradient', t))
}

export function getDisplay1x1Variations(creative, t) {
  return buildSkinsForMedia(
    creative,
    findAllMediaByRatioLabel(getGoogleMedia(creative), '1:1'),
    DISPLAY_1X1_SKINS,
    t,
  )
}

// One group covers both the 1.91:1 "mobile card" family (8 ids) and the
// 1.91:1 "desktop native" family (2 ids) — placementGroups.js groups by
// ratio, not by individual leaf node, per its own documented rationale.
// One full skin-set per matching image (same multi-asset rule as Video).
export function getDisplay191x1Variations(creative, t) {
  return buildSkinsForMedia(
    creative,
    findAllMediaByRatioLabel(getGoogleMedia(creative), '1.91:1'),
    [...RATIO_191_MOBILE_SKINS, ...RATIO_191_DESKTOP_NATIVE_SKINS],
    t,
  )
}

// One skin-set per distinct video — matching the backend's real
// 1-video-per-ad Demand Gen bundling behavior (every video is visible at
// least once), not just the first by array order.
export function getDisplayVideoVariations(creative, t) {
  return buildSkinsForMedia(creative, findAllVideoMedia(getGoogleMedia(creative)), VIDEO_SKINS, t)
}

// Aspect-ratio-agnostic per the epic's own inventory note — reuses a single
// 1.91:1 image (closest match to the "website"/native-card layouts these
// skins use) rather than requiring a dedicated general-purpose asset.
export function getDisplayGeneralVariations(creative, t) {
  const mediaList = getGoogleMedia(creative)
  const media =
    findMediaByRatioLabel(mediaList, '1.91:1') ?? mediaList.find(isImageMedia) ?? mediaList[0]
  if (!media) return []
  return GENERAL_SKINS.map((skin) => buildDisplayAd(creative, media, skin, t))
}
